import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
  type OAuthApi,
} from '@backstage/core-plugin-api';

// ── Cliente da API de gestão de mudanças ────────────────────────────────────
//
// Os tipos são declarados aqui e não importados do pacote de backend: o
// frontend não deve depender do backend em tempo de build, e o que os une é o
// contrato HTTP.

export type TipoMudanca = 'normal' | 'emergencial' | 'padronizada';
export type Criticidade = 'baixa' | 'media' | 'alta' | 'critica' | 'urgente';

/** Abaixo desta antecedência a mudança vai para a fila de aprovação. */
export const HORAS_MINIMAS_DE_ANTECEDENCIA = 24;

/**
 * A janela começa em menos de 24h? O valor do campo `datetime-local` é a hora
 * local de quem agenda — o mesmo relógio do navegador —, então a conta aqui
 * bate com a do servidor, que lê a mesma hora em Brasília.
 */
export function antecedenciaCurta(inicio: string, agora = new Date()): boolean {
  if (!inicio) return false;
  const t = new Date(inicio).getTime();
  return Number.isFinite(t) && t - agora.getTime() < HORAS_MINIMAS_DE_ANTECEDENCIA * 3_600_000;
}

/** Única criticidade aceita dentro do freezing; a mudança fica pendente de aprovação. */
export const CRITICIDADE_DO_FREEZING: Criticidade = 'urgente';
export type StatusMudanca =
  | 'aguardando_aprovacao'
  | 'pendente'
  | 'aprovado'
  | 'em_execucao'
  | 'concluido'
  | 'cancelado'
  | 'falhou'
  | 'reagendado';

/**
 * Um sistema afetado, com o pull request que o altera.
 *
 * O PR é por sistema porque uma GMUD costuma subir várias aplicações de uma
 * vez, cada uma com seu próprio PR.
 */
export interface SistemaAfetado {
  /** Entity ref, ou o nome cru quando veio da planilha antiga. */
  ref: string;
  pullRequestUrl: string | null;
}

export interface PullRequestAberto {
  id: number;
  titulo: string;
  url: string;
  autor: string;
  branchOrigem: string;
  branchDestino: string;
  criadoEm: string;
}

/**
 * Lista de PRs de um sistema.
 *
 * Indisponível não é erro: PAT vencido, sistema fora do Azure ou componente sem
 * anotação devolvem o motivo, e a tela cai no campo de texto livre.
 */
export type ResultadoPrs =
  | { disponivel: true; pullRequests: PullRequestAberto[]; branchAlvo: string }
  | { disponivel: false; motivo: string };

/** PBI / Feature do Azure Boards que a mudança entrega. */
export interface ItemBoard {
  id: number;
  tipo: string;
  titulo: string;
  url: string;
}

/** Itens do board do time; indisponível não é erro (time sem Azure cadastrado etc.). */
export type ResultadoItensBoard =
  | { disponivel: true; itens: ItemBoard[] }
  | { disponivel: false; motivo: string };

export interface Mudanca {
  id: number;
  titulo: string;
  tipo: TipoMudanca;
  descricao: string;
  justificativa: string;
  criticidade: Criticidade;
  status: StatusMudanca;
  objetivoId: number | null;
  objetivoNome?: string | null;
  responsavelRef: string;
  responsavelEmail: string | null;
  timeRef: string | null;
  /** Área responsável em texto livre; nem toda área tem grupo no catálogo. */
  area: string | null;
  sistemas: SistemaAfetado[];
  /** Pessoas/times do catálogo (entity ref) ou e-mails que também recebem o convite. */
  convidados: string[];
  /** PBIs/Features do Azure Boards que a mudança entrega. */
  itensBoard: ItemBoard[];
  inicio: string;
  fim: string;
  planoRollback: string;
  emFreezing: boolean;
  justificativaEmergencial: string | null;
  googleEventId: string | null;
  /** Duração real da execução, em minutos, informada no fechamento. */
  duracaoRealMin: number | null;
  /** Nota de fechamento: o que foi observado durante a execução. */
  observacoes: string | null;
  /** Qual tentativa esta mudança é; a primeira é 0. */
  reagendamentos: number;
  /** Mudança de onde esta veio, quando nasceu de um reagendamento. */
  origemId: number | null;
  /** Id no formulário antigo, quando a mudança veio da importação. */
  origemExterna: string | null;
  /** Decisão da governança, para mudanças agendadas em freezing. */
  aprovacaoPor: string | null;
  /** `freezing`, `antecedencia` (menos de 24h) ou os dois. */
  motivosAprovacao: string[];
  /** Leitura da pipeline de produção: `sucesso`, `falha`, `sem_pipeline` ou nulo. */
  pipelineEstado: string | null;
  aprovacaoEm: string | null;
  aprovacaoObs: string | null;
  criadoPor: string;
  criadoEm: string;
  atualizadoEm: string;
  /** Só na resposta de criação: o convite é efeito colateral e pode falhar. */
  avisoConvite?: string;
}

export interface Objetivo {
  id: number;
  nome: string;
  descricao: string | null;
  ativo: boolean;
  criadoEm: string;
}

export interface JanelaFreezing {
  id: number;
  nome: string;
  inicio: string;
  fim: string;
  motivo: string | null;
  ativo: boolean;
  criadoEm: string;
}

export interface ResultadoFreezing {
  emFreezing: boolean;
  motivos: string[];
  diasBloqueados: string[];
}

export interface Kpis {
  total: number;
  executadas: number;
  concluidas: number;
  falhadas: number;
  canceladas: number;
  reagendadas: number;
  emAndamento: number;
  pendentes: number;
  emFreezing: number;
  taxaSucesso: number | null;
}

export interface FatiaDistribuicao {
  rotulo: string;
  total: number;
  percentual: number;
}

export interface PontoLinhaDoTempo {
  data: string;
  total: number;
  concluidas: number;
  falhadas: number;
  /** Sem desfecho ainda: pendente, aprovado, em execução, cancelado. */
  outras: number;
}

/** Uma linha das tabelas de consolidação (área, objetivo, impacto). */
export interface LinhaConsolidada {
  rotulo: string;
  total: number;
  concluidas: number;
  falhadas: number;
  taxaSucesso: number | null;
}

export interface Duracao {
  mediana: number | null;
  media: number | null;
  medidas: number;
  diasComMudanca: number;
  diasNoPeriodo: number;
  frequencia: number | null;
}

/** Uma célula do mapa de calor área × dia. */
export interface CelulaHeatmap {
  area: string;
  dia: string;
  total: number;
  criticidadeMaxima: Criticidade;
  concluidas: number;
  falhadas: number;
}

export interface Relatorio {
  kpis: Kpis;
  porCriticidade: FatiaDistribuicao[];
  porStatus: FatiaDistribuicao[];
  linhaDoTempo: PontoLinhaDoTempo[];
  porArea: LinhaConsolidada[];
  porObjetivo: LinhaConsolidada[];
  porImpacto: LinhaConsolidada[];
  duracao: Duracao;
  heatmap: CelulaHeatmap[];
  areas: string[];
  mudancas: Mudanca[];
}

export interface DadosReagendamento {
  inicio: string;
  fim: string;
  /** Obrigatório: o relatório sem o porquê só mostra que a data mudou. */
  motivo: string;
  /** Necessária quando a nova janela cai em freezing. */
  justificativaEmergencial?: string;
  /** Revisão da criticidade; em freezing só vale Urgente. */
  criticidade?: Criticidade;
}

export interface DadosFechamento {
  status: 'concluido' | 'falhou' | 'cancelado';
  /** Minutos. Opcional: nem toda execução é cronometrada. */
  duracaoRealMin?: number | null;
  /** Obrigatória quando o desfecho é insucesso. */
  observacoes?: string;
}

export interface FiltroMudancas {
  inicio?: string;
  fim?: string;
  sistema?: string;
  criticidade?: string;
  status?: string;
  responsavel?: string;
}

/** Status em que a mudança já teve desfecho e não se remarca mais. */
const ENCERRADOS: StatusMudanca[] = ['concluido', 'falhou', 'cancelado'];

/**
 * Quem pode reagendar uma mudança: o responsável ou quem a agendou.
 *
 * A tela usa isto só para decidir se mostra o botão. A regra que vale é a do
 * backend, que a reaplica na rota — esconder um botão nunca foi autorização.
 *
 * Refs do catálogo vêm com caixa inconsistente, então a comparação normaliza.
 */
export function podeReagendar(mudanca: Mudanca, usuarioRef: string): boolean {
  if (ENCERRADOS.includes(mudanca.status)) return false;
  const eu = usuarioRef.trim().toLowerCase();
  if (!eu) return false;
  return (
    mudanca.responsavelRef.trim().toLowerCase() === eu ||
    mudanca.criadoPor.trim().toLowerCase() === eu
  );
}

/**
 * Escopo mínimo para criar evento e convidar: cria e edita eventos, e não dá
 * leitura do resto da agenda da pessoa.
 */
const ESCOPO_AGENDA = 'https://www.googleapis.com/auth/calendar.events';

/** Erro que carrega o resultado do freezing, para a tela pedir a justificativa. */
export class ErroFreezing extends Error {
  constructor(
    mensagem: string,
    readonly freezing: ResultadoFreezing,
    /** A recusa foi pela criticidade, e não pela falta de justificativa. */
    readonly exigeUrgente = false,
  ) {
    super(mensagem);
    this.name = 'ErroFreezing';
  }
}

export interface ChangeManagementApi {
  listarMudancas(filtro?: FiltroMudancas): Promise<Mudanca[]>;
  obterMudanca(id: number): Promise<Mudanca>;
  criarMudanca(dados: Record<string, unknown>): Promise<Mudanca>;
  atualizarMudanca(id: number, dados: Record<string, unknown>): Promise<Mudanca>;
  reagendarMudanca(id: number, dados: DadosReagendamento): Promise<Mudanca>;
  fecharMudanca(id: number, dados: DadosFechamento): Promise<Mudanca>;
  pullRequestsDoSistema(sistema: string): Promise<ResultadoPrs>;
  itensDoBoard(timeRef: string, busca?: string): Promise<ResultadoItensBoard>;
  itensDoPullRequest(url: string): Promise<ItemBoard[]>;
  removerMudanca(id: number): Promise<{ removida: number; avisoConvite?: string }>;

  checarFreezing(inicio: string, fim: string): Promise<ResultadoFreezing>;
  diasDeFreezing(inicio: string, fim: string): Promise<Record<string, string[]>>;

  listarObjetivos(todos?: boolean): Promise<Objetivo[]>;
  criarObjetivo(nome: string, descricao?: string): Promise<void>;
  atualizarObjetivo(id: number, dados: Record<string, unknown>): Promise<void>;

  listarJanelas(): Promise<JanelaFreezing[]>;
  criarJanela(dados: Record<string, unknown>): Promise<void>;
  atualizarJanela(id: number, dados: Record<string, unknown>): Promise<void>;
  removerJanela(id: number): Promise<void>;

  diasBloqueados(): Promise<number[]>;
  salvarDiasBloqueados(dias: number[]): Promise<void>;

  relatorio(inicio: string, fim: string): Promise<Relatorio>;
  urlCsv(inicio: string, fim: string): Promise<string>;
}

export const changeManagementApiRef = createApiRef<ChangeManagementApi>({
  id: 'plugin.change-management.service',
});

export class ChangeManagementClient implements ChangeManagementApi {
  constructor(
    private readonly opcoes: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
      /**
       * Opcional: sem ele o convite cai na service account, se houver, ou não
       * acontece. Os testes montam o cliente sem esta dependência.
       */
      googleAuth?: OAuthApi;
    },
  ) {}

  /**
   * Access token Google de quem está usando o portal, para o convite nascer na
   * agenda dela.
   *
   * O consentimento aparece na primeira vez — uma janela do Google pedindo
   * acesso à agenda, disparada de dentro de um clique, então não é bloqueada
   * como popup. Recusar não impede nada: a mudança é agendada do mesmo jeito e
   * a tela avisa que ficou sem convite.
   */
  private async tokenDeAgenda(): Promise<{ token?: string; motivo?: string }> {
    if (!this.opcoes.googleAuth) {
      return { motivo: 'o portal não tem a integração de login Google disponível' };
    }
    try {
      const token = await this.opcoes.googleAuth.getAccessToken(ESCOPO_AGENDA);
      if (token) return { token };
      return { motivo: 'o acesso à agenda não foi concedido' };
    } catch (e: any) {
      // Consentimento recusado, janela fechada, ou escopo não liberado na tela
      // de consentimento do Google. O convite é efeito colateral e não derruba
      // o agendamento — mas o motivo precisa chegar à pessoa, senão ela fica
      // esperando um e-mail que nunca sai.
      return { motivo: e?.message || 'não foi possível obter acesso à sua agenda' };
    }
  }

  private async base(): Promise<string> {
    return this.opcoes.discoveryApi.getBaseUrl('change-management');
  }

  private async pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
    const res = await this.opcoes.fetchApi.fetch(`${await this.base()}${caminho}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });

    if (!res.ok) {
      const corpo = await res.json().catch(() => ({}));
      // 422 é a recusa por freezing; a tela precisa do detalhe para abrir o
      // campo de justificativa em vez de só mostrar um erro genérico.
      if (res.status === 422 && corpo.freezing) {
        throw new ErroFreezing(
          corpo.error ?? 'Agendamento bloqueado',
          corpo.freezing,
          Boolean(corpo.exigeUrgente),
        );
      }
      throw new Error(corpo.error?.message ?? corpo.error ?? `Falha na requisição (${res.status})`);
    }
    return res.status === 204 ? (undefined as T) : res.json();
  }

  private query(params: Record<string, string | undefined>): string {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
    const s = q.toString();
    return s ? `?${s}` : '';
  }

  async listarMudancas(filtro: FiltroMudancas = {}): Promise<Mudanca[]> {
    const { mudancas } = await this.pedir<{ mudancas: Mudanca[] }>(
      `/changes${this.query(filtro as Record<string, string | undefined>)}`,
    );
    return mudancas;
  }

  obterMudanca(id: number) {
    return this.pedir<Mudanca>(`/changes/${id}`);
  }

  async criarMudanca(dados: Record<string, unknown>) {
    const { token, motivo } = await this.tokenDeAgenda();
    return this.pedir<Mudanca>('/changes', {
      method: 'POST',
      body: JSON.stringify({ ...dados, googleAccessToken: token, motivoSemToken: motivo }),
    });
  }

  atualizarMudanca(id: number, dados: Record<string, unknown>) {
    return this.pedir<Mudanca>(`/changes/${id}`, { method: 'PATCH', body: JSON.stringify(dados) });
  }

  pullRequestsDoSistema(sistema: string) {
    return this.pedir<ResultadoPrs>(`/azure/pull-requests${this.query({ sistema })}`);
  }

  itensDoBoard(timeRef: string, busca?: string) {
    return this.pedir<ResultadoItensBoard>(
      `/azure/work-items${this.query({ time: timeRef, busca: busca || undefined })}`,
    );
  }

  async itensDoPullRequest(url: string) {
    const r = await this.pedir<{ itens: ItemBoard[] }>(
      `/azure/pull-request-work-items${this.query({ url })}`,
    );
    return r.itens;
  }

  async fecharMudanca(id: number, dados: DadosFechamento) {
    return this.pedir<Mudanca>(`/changes/${id}/close`, {
      method: 'POST',
      // O token só faz falta no cancelamento, que tira o evento da agenda de
      // quem foi convidado; pedi-lo sempre evita um caminho especial.
      body: JSON.stringify({ ...dados, googleAccessToken: (await this.tokenDeAgenda()).token }),
    });
  }

  async reagendarMudanca(id: number, dados: DadosReagendamento) {
    const { token, motivo } = await this.tokenDeAgenda();
    return this.pedir<Mudanca>(`/changes/${id}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ ...dados, googleAccessToken: token, motivoSemToken: motivo }),
    });
  }

  async removerMudanca(id: number) {
    // O token vai junto para o convite ser cancelado na agenda de quem foi
    // convidado; sem ele o evento sobrevive à mudança.
    const { token } = await this.tokenDeAgenda();
    return this.pedir<{ removida: number; avisoConvite?: string }>(`/changes/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ googleAccessToken: token }),
    });
  }

  checarFreezing(inicio: string, fim: string) {
    return this.pedir<ResultadoFreezing>(`/freezing/check${this.query({ inicio, fim })}`);
  }

  async diasDeFreezing(inicio: string, fim: string) {
    const { dias } = await this.pedir<{ dias: Record<string, string[]> }>(
      `/freezing/calendar${this.query({ inicio, fim })}`,
    );
    return dias;
  }

  async listarObjetivos(todos = false) {
    const { objetivos } = await this.pedir<{ objetivos: Objetivo[] }>(
      `/objectives${todos ? '?todos=true' : ''}`,
    );
    return objetivos;
  }

  async criarObjetivo(nome: string, descricao?: string) {
    await this.pedir('/objectives', { method: 'POST', body: JSON.stringify({ nome, descricao }) });
  }

  async atualizarObjetivo(id: number, dados: Record<string, unknown>) {
    await this.pedir(`/objectives/${id}`, { method: 'PATCH', body: JSON.stringify(dados) });
  }

  async listarJanelas() {
    const { janelas } = await this.pedir<{ janelas: JanelaFreezing[] }>('/freezing/windows');
    return janelas;
  }

  async criarJanela(dados: Record<string, unknown>) {
    await this.pedir('/freezing/windows', { method: 'POST', body: JSON.stringify(dados) });
  }

  async atualizarJanela(id: number, dados: Record<string, unknown>) {
    await this.pedir(`/freezing/windows/${id}`, { method: 'PATCH', body: JSON.stringify(dados) });
  }

  async removerJanela(id: number) {
    await this.pedir<void>(`/freezing/windows/${id}`, { method: 'DELETE' });
  }

  async diasBloqueados() {
    const { diasBloqueados } = await this.pedir<{ diasBloqueados: number[] }>('/freezing/weekdays');
    return diasBloqueados;
  }

  async salvarDiasBloqueados(dias: number[]) {
    await this.pedir('/freezing/weekdays', {
      method: 'PUT',
      body: JSON.stringify({ diasBloqueados: dias }),
    });
  }

  relatorio(inicio: string, fim: string) {
    return this.pedir<Relatorio>(`/reports${this.query({ inicio, fim })}`);
  }

  async urlCsv(inicio: string, fim: string) {
    return `${await this.base()}/reports/csv${this.query({ inicio, fim })}`;
  }
}
